import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { HiFilter } from 'react-icons/hi'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair, seoBreadcrumbTriple } from '../../utils/seoUtils'
import './MartialArts.css'

const disciplines = [
  {
    id: 'kubo',
    name: 'КУБО',
    nameEn: 'KUBO',
    nameJa: 'КУБО',
    subtitle: 'Classical Universal Wrestling',
    subtitleRu: 'Классическая Универсальная Борьба',
    subtitleJa: '古典的ユニバーサルレスリング',
    category: 'grappling',
    description: 'КУБО (Классическая Универсальная Борьба) - комплексная система боевых искусств, объединяющая элементы различных видов борьбы. Система включает техники бросков, удержаний, болевых и удушающих приемов, а также работу в партере.',
    descriptionEn: 'KUBO (Classical Universal Wrestling) - a comprehensive martial arts system combining elements of various wrestling styles. The system includes throwing techniques, holds, joint locks, chokes, and ground work.',
    descriptionJa: 'КУБО（古典的ユニバーサルレスリング）は、様々な格闘技スタイルの要素を組み合わせた包括的な武道システムです。システムには投げ技、抑え技、関節技、絞め技、および寝技が含まれます。',
    history: 'КУБО была разработана как универсальная система, объединяющая лучшие техники из различных видов борьбы. Система включает элементы классической борьбы, дзюдо, самбо и других боевых искусств.',
    historyEn: 'KUBO was developed as a universal system combining the best techniques from various wrestling styles. The system includes elements of classical wrestling, judo, sambo, and other martial arts.',
    historyJa: 'КУБОは、様々な格闘技スタイルから最良の技術を組み合わせた普遍的なシステムとして開発されました。システムには古典的格闘技、柔道、サンボ、その他の武道の要素が含まれます。',
    techniques: ['Броски (nage-waza)', 'Удержания (osaekomi-waza)', 'Болевые приемы (kansetsu-waza)', 'Удушающие приемы (shime-waza)', 'Работа в партере (ne-waza)'],
    techniquesEn: ['Throws (nage-waza)', 'Holds (osaekomi-waza)', 'Joint locks (kansetsu-waza)', 'Chokes (shime-waza)', 'Ground work (ne-waza)'],
    techniquesJa: ['投げ技（投げ技）', '抑え技（抑え込み技）', '関節技（関節技）', '絞め技（絞め技）', '寝技（寝技）']
  },
  {
    id: 'aikido',
    name: 'Айкидо',
    nameEn: 'Aikido',
    nameJa: '合気道',
    category: 'traditional',
    description: 'Айкидо - японское боевое искусство, основанное на принципах гармонии и использования силы противника. Техники айкидо включают броски, болевые приемы и работу с оружием.',
    descriptionEn: 'Aikido - a Japanese martial art based on principles of harmony and using the opponent\'s force. Aikido techniques include throws, joint locks, and weapon work.',
    descriptionJa: '合気道は、調和の原則と相手の力を利用することに基づく日本の武道です。合気道の技術には投げ技、関節技、武器の使用が含まれます。',
    history: 'Айкидо было создано Морихеем Уэсибой в начале 20 века, опираясь на традиционные японские боевые искусства. Оно подчеркивает гармонию, духовное развитие и ненаступательную защиту.',
    historyEn: 'Aikido was created by Morihei Ueshiba in the early 20th century, drawing from traditional Japanese martial arts. It emphasizes harmony, spiritual development, and non-aggressive defense.',
    historyJa: '合気道は、20世紀初頭に植芝盛平によって創設され、伝統的な日本の武道から発展しました。調和、精神的発達、非攻撃的な防御を強調しています。',
    techniques: ['Иккё (первый контроль)', 'Никё (второй контроль)', 'Санкё (третий контроль)', 'Йонкё (четвертый контроль)', 'Котэ-гаэси (обратный захват запястья)'],
    techniquesEn: ['Ikkyo (first control)', 'Nikyo (second control)', 'Sankyo (third control)', 'Yonkyo (fourth control)', 'Kote-gaeshi (wrist reversal)'],
    techniquesJa: ['一教（第一制御）', '二教（第二制御）', '三教（第三制御）', '四教（第四制御）', '小手返し（手首の逆転）']
  },
  {
    id: 'aikijutsu',
    name: 'Айки-дзюцу',
    nameEn: 'Aiki-jutsu',
    nameJa: '合気術',
    category: 'traditional',
    description: 'Айки-дзюцу - традиционное японское боевое искусство, предшественник айкидо. Фокусируется на практических боевых техниках, включая броски, болевые приемы и работу с оружием.',
    descriptionEn: 'Aiki-jutsu - traditional Japanese martial art, predecessor to aikido. Focuses on practical combat techniques, including throws, joint locks, and weapon work.',
    descriptionJa: '合気術は、合気道の前身である伝統的な日本の武道です。投げ技、関節技、武器の使用を含む実践的な戦闘技術に焦点を当てています。',
    history: 'Айки-дзюцу является предшественником айкидо и включает более боевые и практичные техники. Сохраняет традиционные элементы японских боевых искусств с акцентом на эффективность в реальном бою. Развивалось как боевая система для самураев, сочетающая принципы айки с практическими техниками дзю-дзюцу.',
    historyEn: 'Aiki-jutsu is the predecessor to aikido and includes more combative and practical techniques. Preserves traditional elements of Japanese martial arts with emphasis on effectiveness in real combat. Developed as a combat system for samurai, combining aiki principles with practical jujutsu techniques.',
    historyJa: '合気術は合気道の前身であり、より戦闘的で実践的な技術を含んでいます。実戦での効果を重視しながら、日本の武道の伝統的要素を保持しています。武士のための戦闘システムとして発展し、合気の原則と実践的な柔術技術を組み合わせています。',
    techniques: ['Иккё (первый контроль)', 'Никё (второй контроль)', 'Санкё (третий контроль)', 'Атэми (удары по жизненно важным точкам)', 'Нагэ-вадза (броски)', 'Кансэцу-вадза (болевые приемы)'],
    techniquesEn: ['Ikkyo (first control)', 'Nikyo (second control)', 'Sankyo (third control)', 'Atemi (strikes to vital points)', 'Nage-waza (throws)', 'Kansetsu-waza (joint locks)'],
    techniquesJa: ['一教（第一制御）', '二教（第二制御）', '三教（第三制御）', '当身（急所への打撃）', '投げ技（投げ技）', '関節技（関節技）']
  },
  {
    id: 'jujutsu',
    name: 'Дзю-дзюцу',
    nameEn: 'Jujutsu',
    nameJa: '柔術',
    category: 'grappling',
    description: 'Дзю-дзюцу - традиционное японское боевое искусство, включающее техники бросков, удержаний и болевых приемов. Также включает работу с оружием и техники самообороны.',
    descriptionEn: 'Jujutsu - traditional Japanese martial art including throwing, holding, and joint lock techniques. Also includes weapon work and self-defense techniques.',
    descriptionJa: '柔術は、投げ技、抑え技、関節技を含む伝統的な日本の武道です。武器の使用と自己防衛技術も含まれます。',
    history: 'Дзю-дзюцу - это семейство японских боевых искусств, возникших среди самураев феодальной Японии. Оно было разработано для ближнего боя в ситуациях, когда оружие могло быть потеряно или непрактично.',
    historyEn: 'Jujutsu is a family of Japanese martial arts that originated among the samurai of feudal Japan. It was developed for close combat in situations where a weapon might be lost or impractical.',
    historyJa: '柔術は、封建時代の日本の武士の間で生まれた日本の武道の一族です。武器が失われるか実用的でない状況での近接戦闘のために開発されました。',
    techniques: ['Атэми (удары)', 'Нагэ-вадза (броски)', 'Катамэ-вадза (удержания)', 'Кансэцу-вадза (болевые приемы)', 'Симе-вадза (удушающие приемы)'],
    techniquesEn: ['Atemi (strikes)', 'Nage-waza (throws)', 'Katame-waza (holds)', 'Kansetsu-waza (joint locks)', 'Shime-waza (chokes)'],
    techniquesJa: ['当身（打撃）', '投げ技（投げ技）', '固め技（抑え技）', '関節技（関節技）', '絞め技（絞め技）']
  },
  {
    id: 'aikibujutsu',
    name: 'Айки-бу-дзюцу',
    nameEn: 'Aiki-bu-jutsu',
    nameJa: '合気武術',
    category: 'traditional',
    description: 'Айки-бу-дзюцу - комплексная система, объединяющая техники айкидо и работы с оружием. Включает техники с дзё (палка), боккэн (деревянный меч) и танто (нож).',
    descriptionEn: 'Aiki-bu-jutsu - a comprehensive system combining aikido techniques and weapon work. Includes techniques with jo (staff), bokken (wooden sword), and tanto (knife).',
    descriptionJa: '合気武術は、合気道の技術と武器の使用を組み合わせた包括的なシステムです。杖（棒）、木剣（木製の剣）、短刀（ナイフ）の技術が含まれます。',
    history: 'Айки-бу-дзюцу развилось из айкидо, включив в себя традиционные техники работы с оружием из различных японских боевых искусств.',
    historyEn: 'Aiki-bu-jutsu developed from aikido, incorporating traditional weapon techniques from various Japanese martial arts.',
    historyJa: '合気武術は、様々な日本の武道から伝統的な武器技術を取り入れて、合気道から発展しました。',
    techniques: ['Дзё-вадза (техники с палкой)', 'Боккэн-вадза (техники с деревянным мечом)', 'Танто-вадза (техники с ножом)', 'Тай-дзюцу (техники без оружия)', 'Рэнраку-вадза (связки техник)'],
    techniquesEn: ['Jo-waza (staff techniques)', 'Bokken-waza (wooden sword techniques)', 'Tanto-waza (knife techniques)', 'Tai-jutsu (unarmed techniques)', 'Renraku-waza (technique combinations)'],
    techniquesJa: ['杖技（棒の技術）', '木剣技（木製の剣の技術）', '短刀技（ナイフの技術）', '体術（武器なしの技術）', '連絡技（技術の組み合わせ）']
  },
  {
    id: 'karatejutsu',
    name: 'Карате-дзюцу',
    nameEn: 'Karate-jutsu',
    nameJa: '空手術',
    category: 'striking',
    description: 'Карате-дзюцу - боевое искусство, фокусирующееся на эффективных техниках ударов и блоков. Включает ката (формальные комплексы), кумитэ (спарринг) и работу с оружием.',
    descriptionEn: 'Karate-jutsu - a martial art focusing on effective striking and blocking techniques. Includes kata (formal patterns), kumite (sparring), and weapon work.',
    descriptionJa: '空手術は、効果的な打撃と防御技術に焦点を当てた武道です。型（正式なパターン）、組手（スパーリング）、武器の使用が含まれます。',
    history: 'Карате-дзюцу развилось из окинавского карате, включив в себя более боевые и практичные техники. Сохраняет традиционные элементы, но с акцентом на эффективность.',
    historyEn: 'Karate-jutsu developed from Okinawan karate, incorporating more combative and practical techniques. Preserves traditional elements but with emphasis on effectiveness.',
    historyJa: '空手術は、より戦闘的で実践的な技術を取り入れて、沖縄空手から発展しました。伝統的要素を保持しながら、効果を重視しています。',
    techniques: ['Цуки (удары руками)', 'Гэри (удары ногами)', 'Укэ (блоки)', 'Нагэ-вадза (броски)', 'Катамэ-вадза (удержания)'],
    techniquesEn: ['Tsuki (hand strikes)', 'Geri (kicks)', 'Uke (blocks)', 'Nage-waza (throws)', 'Katame-waza (holds)'],
    techniquesJa: ['突き（手の打撃）', '蹴り（足の打撃）', '受け（防御）', '投げ技（投げ技）', '固め技（抑え技）']
  },
  {
    id: 'kenjutsu',
    name: 'Кэн-дзюцу',
    nameEn: 'Kenjutsu',
    nameJa: '剣術',
    category: 'traditional',
    description: 'Кэн-дзюцу - японское искусство владения мечом, включающее различные техники фехтования. Изучается с использованием боккэна (деревянный меч) и иайто (затупленный меч).',
    descriptionEn: 'Kenjutsu - Japanese sword art including various fencing techniques. Studied using bokken (wooden sword) and iaito (blunt sword).',
    descriptionJa: '剣術は、様々な剣術技術を含む日本の剣の芸術です。木剣（木製の剣）と居合刀（鈍い剣）を使用して学習されます。',
    history: 'Кэн-дзюцу - одно из древнейших японских боевых искусств, развивавшееся самураями. Включает множество школ с различными стилями и техниками.',
    historyEn: 'Kenjutsu is one of the oldest Japanese martial arts, developed by samurai. Includes many schools with different styles and techniques.',
    historyJa: '剣術は、武士によって発展した最も古い日本の武道の一つです。様々なスタイルと技術を持つ多くの流派が含まれます。',
    techniques: ['Сюри-кэн (прямые удары)', 'Кэса-гири (диагональные удары)', 'Цуки (колющие удары)', 'Укэ-нагаси (отводы)', 'Иай (техники выхватывания меча)'],
    techniquesEn: ['Shuri-ken (straight cuts)', 'Kesa-giri (diagonal cuts)', 'Tsuki (thrusts)', 'Uke-nagashi (deflections)', 'Iai (sword drawing techniques)'],
    techniquesJa: ['直斬り（直線の斬り）', '袈裟斬り（斜めの斬り）', '突き（突き刺し）', '受け流し（防御）', '居合（剣を抜く技術）']
  },
  {
    id: 'kempo',
    name: 'Кемпо',
    nameEn: 'Kempo',
    nameJa: '拳法',
    category: 'striking',
    description: 'Кемпо - боевое искусство, сочетающее удары руками и ногами с техниками самообороны. Включает элементы карате, кунг-фу и других боевых искусств.',
    descriptionEn: 'Kempo - a martial art combining hand and foot strikes with self-defense techniques. Includes elements of karate, kung fu, and other martial arts.',
    descriptionJa: '拳法は、手と足の打撃と自己防衛技術を組み合わせた武道です。空手、功夫、その他の武道の要素が含まれます。',
    history: 'Кемпо имеет китайские корни и было адаптировано в Японии. Сочетает жесткие и мягкие техники, включая удары, блоки, броски и болевые приемы.',
    historyEn: 'Kempo has Chinese roots and was adapted in Japan. Combines hard and soft techniques, including strikes, blocks, throws, and joint locks.',
    historyJa: '拳法は中国のルーツを持ち、日本で適応されました。打撃、防御、投げ技、関節技を含む硬い技術と柔らかい技術を組み合わせています。',
    techniques: ['Удары руками (тэ-вадза)', 'Удары ногами (аси-вадза)', 'Блоки (укэ-вадза)', 'Броски (нагэ-вадза)', 'Болевые приемы (кансэцу-вадза)'],
    techniquesEn: ['Hand strikes (te-waza)', 'Kicks (ashi-waza)', 'Blocks (uke-waza)', 'Throws (nage-waza)', 'Joint locks (kansetsu-waza)'],
    techniquesJa: ['手技（手の打撃）', '足技（足の打撃）', '受け技（防御）', '投げ技（投げ技）', '関節技（関節技）']
  }
]

function MartialArts() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const { disciplineId } = useParams()
  const [selectedCategory, setSelectedCategory] = useState('all')

  const filteredDisciplines = selectedCategory === 'all' 
    ? disciplines 
    : disciplines.filter(d => d.category === selectedCategory)

  const displayedDiscipline = disciplineId 
    ? disciplines.find(d => d.id === disciplineId)
    : null

  const isRussian = i18n.language === 'ru'
  const isJapanese = i18n.language === 'ja'

  const listBreadcrumbs = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.martialArts'), 'martial-arts'),
    [t]
  )
  const detailBreadcrumbs = useMemo(() => {
    if (!displayedDiscipline) return listBreadcrumbs
    const leaf = isJapanese
      ? displayedDiscipline.nameJa || displayedDiscipline.nameEn
      : isRussian
        ? displayedDiscipline.name
        : displayedDiscipline.nameEn
    return seoBreadcrumbTriple(
      t('common.home'),
      t('common.martialArts'),
      'martial-arts',
      leaf,
      `martial-arts/${displayedDiscipline.id}`
    )
  }, [displayedDiscipline, t, isJapanese, isRussian, listBreadcrumbs])

  if (displayedDiscipline) {
    return (
      <div className="martial-arts-page">
        <SEO 
          title={isRussian ? displayedDiscipline.name : displayedDiscipline.nameEn}
          breadcrumbItems={detailBreadcrumbs}
        />
        <div className="container">
          <Link to={getLocalizedLink('/martial-arts')} className="back-link">← {t('martialArts.back', 'Back to Martial Arts')}</Link>
          <div className="discipline-detail">
            <h1>{isJapanese ? (displayedDiscipline.nameJa || displayedDiscipline.nameEn) : (isRussian ? displayedDiscipline.name : displayedDiscipline.nameEn)}</h1>
            {displayedDiscipline.subtitle && (
              <p className="discipline-subtitle">{isJapanese ? (displayedDiscipline.subtitleJa || displayedDiscipline.subtitle) : (isRussian ? (displayedDiscipline.subtitleRu || displayedDiscipline.subtitle) : displayedDiscipline.subtitle)}</p>
            )}
            <p className="discipline-description">
              {isJapanese ? (displayedDiscipline.descriptionJa || displayedDiscipline.descriptionEn) : (isRussian ? displayedDiscipline.description : displayedDiscipline.descriptionEn)}
            </p>

            {displayedDiscipline.history && (
              <section className="discipline-section">
                <h2>{t('martialArts.history', 'History')}</h2>
                <p>{isJapanese ? (displayedDiscipline.historyJa || displayedDiscipline.historyEn) : (isRussian ? displayedDiscipline.history : displayedDiscipline.historyEn)}</p>
              </section>
            )}

            {displayedDiscipline.techniques && displayedDiscipline.techniques.length > 0 && (
              <section className="discipline-section">
                <h2>{t('martialArts.techniques', 'Key Techniques')}</h2>
                <ul className="techniques-list">
                  {displayedDiscipline.techniques.map((technique, index) => (
                    <li key={index}>
                      {isJapanese 
                        ? (displayedDiscipline.techniquesJa && displayedDiscipline.techniquesJa[index] || displayedDiscipline.techniquesEn && displayedDiscipline.techniquesEn[index] || technique)
                        : (isRussian ? technique : (displayedDiscipline.techniquesEn && displayedDiscipline.techniquesEn[index] || technique))}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="discipline-actions">
              <Link to={`${getLocalizedLink('/certifications')}/${displayedDiscipline.id}`} className="btn btn-primary">
                {t('common.certifications')}
              </Link>
              <Link to={`${getLocalizedLink('/schools')}/${displayedDiscipline.id}`} className="btn btn-secondary">
                {t('common.schools')}
              </Link>
              <Link to={`${getLocalizedLink('/books')}/${displayedDiscipline.id}`} className="btn btn-secondary">
                {t('common.books')}
              </Link>
              <Link to={`${getLocalizedLink('/black-belts')}/${displayedDiscipline.id}`} className="btn btn-secondary">
                {t('common.blackBelts')}
              </Link>
              <Link to={`${getLocalizedLink('/colored-belts')}/${displayedDiscipline.id}`} className="btn btn-secondary">
                {t('common.coloredBelts')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="martial-arts-page">
      <SEO 
        title={t('common.martialArts')}
        description={t('martialArts.description', 'Explore the martial arts disciplines of SUMA International')}
        breadcrumbItems={listBreadcrumbs}
      />
      <div className="container">
        <div className="martial-arts-header">
          <h1>{t('common.martialArts')}</h1>
          <p className="page-description">
            {t('martialArts.intro', 'SUMA International encompasses various martial arts disciplines, each with its own rich history and tradition.')}
          </p>
        </div>

        <div className="filter-section">
          <HiFilter className="filter-icon" />
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              {t('martialArts.all', 'All')}
            </button>
            <button 
              className={`filter-btn ${selectedCategory === 'striking' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('striking')}
            >
              {t('martialArts.striking', 'Striking')}
            </button>
            <button 
              className={`filter-btn ${selectedCategory === 'grappling' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('grappling')}
            >
              {t('martialArts.grappling', 'Grappling')}
            </button>
            <button 
              className={`filter-btn ${selectedCategory === 'traditional' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('traditional')}
            >
              {t('martialArts.traditional', 'Traditional')}
            </button>
          </div>
        </div>

        <div className="disciplines-grid">
          {filteredDisciplines.map(discipline => (
            <Link 
              key={discipline.id}
              to={`${getLocalizedLink('/martial-arts')}/${discipline.id}`}
              className="discipline-card"
            >
              <div className="discipline-header">
                <h3>{isJapanese ? (discipline.nameJa || discipline.nameEn) : (isRussian ? discipline.name : discipline.nameEn)}</h3>
                {discipline.subtitle && (
                  <p className="discipline-card-subtitle">{isJapanese ? (discipline.subtitleJa || discipline.subtitle) : (isRussian ? (discipline.subtitleRu || discipline.subtitle) : discipline.subtitle)}</p>
                )}
              </div>
              <p className="discipline-card-description">
                {isJapanese ? (discipline.descriptionJa || discipline.descriptionEn) : (isRussian ? discipline.description : discipline.descriptionEn)}
              </p>
              <span className="discipline-link-text">
                {t('martialArts.learnMore', 'Learn more')} →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export default MartialArts
